import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for managing family selection and view mode
 * Handles which family is selected and whether we're in edit mode
 */
export function useFamilySelection(families, projects, activeProjectId) {
    const [searchParams] = useSearchParams();
    const familyIdParam = searchParams.get('familyId');

    // Selection & View States
    const [selectedFamilyId, setSelectedFamilyId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isCreatingFamily, setIsCreatingFamily] = useState(false);
    const [newFamilyName, setNewFamilyName] = useState('');

    // Family Editing State (Header In-Place Editing)
    const [isEditingName, setIsEditingName] = useState(false);
    const [editFamilyName, setEditFamilyName] = useState('');
    const [savingFamilyName, setSavingFamilyName] = useState(false);

    // Auto-select only when the current family is not valid for the active project.
    useEffect(() => {
        const nextProjId = activeProjectId;
        
        if (nextProjId && projects.length > 0 && families.length > 0) {
            const activeProj = projects.find(p => p.id.toString() === nextProjId.toString());
            if (activeProj) {
                const linkedFamilyIds = (activeProj.linkedFamilies || []).map(id => id.toString());
                const projectFamilies = families.filter(f => {
                    const familyId = f.id?.toString();
                    const parentId = f.parentId?.toString();
                    return linkedFamilyIds.includes(familyId) || (parentId && linkedFamilyIds.includes(parentId));
                });

                setSelectedFamilyId(prev => {
                    if (familyIdParam && projectFamilies.some(f => f.id?.toString() === familyIdParam.toString())) {
                        return projectFamilies.find(f => f.id?.toString() === familyIdParam.toString())?.id || prev;
                    }
                    if (projectFamilies.some(f => f.id?.toString() === prev?.toString())) {
                        return prev;
                    }
                    return projectFamilies[0]?.id || null;
                });
            } else {
                setSelectedFamilyId(null);
            }
        } else if (!nextProjId && families.length > 0) {
            setSelectedFamilyId(prev => {
                if (familyIdParam && families.some(f => f.id?.toString() === familyIdParam.toString())) {
                    return families.find(f => f.id?.toString() === familyIdParam.toString())?.id || prev;
                }
                if (families.some(f => f.id?.toString() === prev?.toString())) {
                    return prev;
                }
                return families[0].id;
            });
        } else {
            setSelectedFamilyId(null);
        }
    }, [activeProjectId, projects, families, familyIdParam]);

    return {
        selectedFamilyId,
        setSelectedFamilyId,
        isEditMode,
        setIsEditMode,
        isCreatingFamily,
        setIsCreatingFamily,
        newFamilyName,
        setNewFamilyName,
        isEditingName,
        setIsEditingName,
        editFamilyName,
        setEditFamilyName,
        savingFamilyName,
        setSavingFamilyName
    };
}
